/**
 * Credential Encryption Utility
 * Obfuscates username and password before sending to server
 * OWASP A02:2021 - Cryptographic Failures
 * 
 * Note: This provides obfuscation, not security. Real security comes from HTTPS/TLS.
 * This makes credentials harder to read in network traces and logs.
 */

/**
 * Encrypts a string using AES-GCM
 * @param {string} plaintext - The text to encrypt
 * @param {string} keyString - The encryption key (from config)
 * @returns {Promise<string>} Base64-encoded encrypted data with IV
 */
export async function encryptCredential(plaintext, keyString = 'metapath-credential-key-2024') {
    if (!plaintext) {
        return plaintext;
    }

    try {
        // Derive key from string using PBKDF2
        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            encoder.encode(keyString),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        // Derive AES key
        const salt = encoder.encode('metapath-salt-2024'); // Static salt for consistency
        const key = await crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt']
        );

        // Generate random IV
        const iv = crypto.getRandomValues(new Uint8Array(12));

        // Encrypt
        const encrypted = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv
            },
            key,
            encoder.encode(plaintext)
        );

        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encrypted), iv.length);

        // Return base64 encoded
        return btoa(String.fromCharCode(...combined));
    } catch (error) {
        console.error('Error encrypting credential:', error);
        // Fallback: return base64 encoded (weak obfuscation)
        return btoa(unescape(encodeURIComponent(plaintext)));
    }
}

/**
 * Encrypts both username and password
 * @param {string} username - Username to encrypt
 * @param {string} password - Password to encrypt
 * @returns {Promise<{username: string, password: string}>} Encrypted credentials
 */
export async function encryptCredentials(username, password) {
    try {
        const [encryptedUsername, encryptedPassword] = await Promise.all([
            encryptCredential(username),
            encryptCredential(password)
        ]);

        return {
            username: encryptedUsername,
            password: encryptedPassword
        };
    } catch (error) {
        console.error('Error encrypting credentials:', error);
        // Fallback: return as-is (should not happen, but graceful degradation)
        return {
            username: username,
            password: password
        };
    }
}

/**
 * Checks if encryption is enabled (can be controlled via config)
 * @returns {boolean} True if encryption should be used
 */
export function isEncryptionEnabled() {
    // Can be controlled via environment variable or config
    return true; // Always enabled for security
}

